import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Drawer,
  Grid,
  Button,
} from '@mui/material';

// Icons MUI
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EmailIcon from '@mui/icons-material/Email';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppPagination from '../../components/base/AppPagination';
import { usePagination } from '../../hooks/usePagination';
import AppSearch from '../../components/base/AppSearch';
import FileDownloadIcon from '@mui/icons-material/FileDownload'; // Composant utilitaire pour le Drawer
const DetailItem = ({ label, value, icon }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 1.2,
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      {icon &&
        React.cloneElement(icon, {
          sx: { color: '#A3AED0', fontSize: '18px' },
        })}
      <Typography sx={{ color: '#A3AED0', fontSize: '14px', fontWeight: 500 }}>
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ color: '#2B3674', fontSize: '14px', fontWeight: 700 }}>
      {value || '—'}
    </Typography>
  </Box>
);

const Factures = () => {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const isIntervenant = user?.role === 3;

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  const rolePath = useMemo(() => {
    // On vérifie si role_user est 1 (Super Admin) ou 2 (Admin)
    const isAdmin = user?.role === 1 || user?.role === 2;

    return isAdmin ? 'admin' : 'intervenant';
  }, [user]);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/${rolePath}/offer/command/invoice/l/list_invoice`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setInvoices(response.data.result || []);
    } catch (err) {
      toast.error('Erreur lors du chargement des factures');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, rolePath]);

  useEffect(() => {
    if (user) fetchInvoices();
  }, [user, fetchInvoices]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const search = searchTerm.toLowerCase();
      const clientFull =
        `${inv.client?.nom_client} ${inv.client?.prenom_client}`.toLowerCase();
      return (
        inv.ref_cmd_invoice.toLowerCase().includes(search) ||
        clientFull.includes(search)
      );
    });
  }, [searchTerm, invoices]);

  const itemsPerPage = 8;
  const { next, currentData, currentPage, count, totalItems } = usePagination(
    filteredInvoices,
    itemsPerPage
  );

  const getStatusStyles = (status) => {
    switch (status) {
      case 'PAID':
        return { bgcolor: '#E6F9F0', color: '#05CD99', label: 'Payée' };
      case 'CREATED':
        return { bgcolor: '#FFF8E6', color: '#FFB800', label: 'Créée' };
      case 'REFUNDED':
        return { bgcolor: '#FFF5F5', color: '#EE5D50', label: 'Remboursée' };
      default:
        return { bgcolor: '#F4F7FE', color: '#A3AED0', label: status };
    }
  };

  const DetailRow = ({ label, value, icon }) => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ color: '#A3AED0', display: 'flex' }}>{icon}</Box>
      <Typography sx={{ color: '#707EAE', fontSize: '13px', width: '60px' }}>
        {label}:
      </Typography>
      <Typography sx={{ color: '#2B3674', fontWeight: 700, fontSize: '13px' }}>
        {value}
      </Typography>
    </Stack>
  );
  const handleDownloadPDF = async (invoiceToDownload = null) => {
    // 1. On récupère l'objet (soit l'argument, soit l'état sélectionné)
    const target = invoiceToDownload || selectedInvoice;
    // 2. On récupère la référence (on check toutes les clés possibles : ref_cmd ou ref_cmd_invoice)
    const reference = target?.ref_cmd || target?.ref_cmd_invoice;
    if (!reference) {
      toast.error('Référence de facture introuvable');
      return;
    }

    try {
      // Log pour vérifier l'URL dans la console avant l'appel
      const finalUrl = `${apiUrl}/admin/offer/command/invoice/${reference}`;
      console.log('Tentative de téléchargement :', finalUrl);

      const response = await axios.get(finalUrl, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      // Création du Blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // --- CHOIX : OUVRIR OU TÉLÉCHARGER ---

      // Option A: Ouvrir dans un nouvel onglet pour aperçu
      window.open(url, '_blank');

      // Option B: Téléchargement forcé
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Facture_${reference}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Nettoyage
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur PDF Details:', err.response);
      toast.error(
        'Erreur lors de la génération de la facture (404 ou Serveur)'
      );
    }
  };const getTypeCmdLabel = (type) => {
  switch (type) {
    case 'workshop':
      return 'Atelier';
    case 'offer':
      return 'Consultation';
    case 'subscription':
      return 'Abonnement';
    case 'PACK_VIDEOS':
      return 'Pack jetons';
    default:
      return type || '—';
  }
};
  return (
    <Box sx={{ pt: { xs: '80px', md: '20px' }, px: 3, pb: 4 }}>
      {/* --- HEADER --- */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 4 }}
        spacing={2}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <AppSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Référence, client..."
          />
        </Stack>
      </Stack>

      {/* --- TABLEAU --- */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          border: '1px solid #F4F7FE',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#F4F7FE' }}>
              <TableRow>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  RÉFÉRENCE
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  CLIENT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  MONTANT TTC
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  DATE
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  STATUT
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#A3AED0', fontWeight: 700 }}
                >
                  ACTION
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress
                      size={30}
                      sx={{ my: 4, color: '#4318FF' }}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                currentData().map((inv) => {
                  const status = getStatusStyles(inv.status_invoice);
                  return (
                    <TableRow key={inv.id_invoice} hover>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 700 }}>
                        {inv.ref_cmd_invoice}
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                        {isIntervenant
                          ? inv.client?.prenom_client
                          : `${inv.client?.prenom_client} ${inv.client?.nom_client}`}
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 800 }}>
                        {inv.total_ca_invoice}€
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674' }}>
                        {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={status.label}
                          size="small"
                          sx={{
                            bgcolor: status.bgcolor,
                            color: status.color,
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          {/* Bouton Télécharger (Direct) */}
                          {!isIntervenant && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => handleDownloadPDF(inv)}
                              sx={{ borderRadius: '10px', fontWeight: 600 }}
                            >
                              PDF
                            </Button>
                          )}

                          {/* Bouton Voir Détails */}
                          <IconButton
                            onClick={() => {
                              setSelectedInvoice(inv);
                              setOpenDrawer(true);
                            }}
                            sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <AppPagination
          count={count}
          page={currentPage}
          onChange={next}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
        />
      </Paper>

      {/* --- DRAWER DÉTAILS --- */}
      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        sx={{ zIndex: 3000 }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 550 },
            bgcolor: '#FAFBFF', // Fond très légèrement bleuté pour le luxe
            boxShadow: '-10px 0px 30px rgba(0,0,0,0.05)',
          },
        }}
      >
        {selectedInvoice && (
          <Box
            sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {/* HEADER */}
            <Box
              sx={{
                p: 3,
                bgcolor: '#fff',
                borderBottom: '1px solid #E9EDF7',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#4318FF',
                    fontSize: '12px',
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    mb: 0.5,
                  }}
                >
                  Document Officiel
                </Typography>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 800, color: '#2B3674' }}
                >
                  Facture {selectedInvoice.ref_cmd_invoice}
                </Typography>
              </Box>
              <IconButton
                onClick={() => setOpenDrawer(false)}
                sx={{ bgcolor: '#F4F7FE', borderRadius: '12px' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
              {/* STATUS BAR */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 4,
                  borderRadius: '16px',
                  border: '1px dashed #E0E5F2',
                  bgcolor: '#fff',
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Typography
                      sx={{
                        color: '#A3AED0',
                        fontSize: '13px',
                        fontWeight: 500,
                      }}
                    >
                      Statut du paiement
                    </Typography>
                    <Typography sx={{ color: '#2B3674', fontWeight: 700 }}>
                      {selectedInvoice.status_invoice === 'PAID'
                        ? 'Règlement effectué'
                        : 'En attente'}
                    </Typography>
                  </Box>
                  <Chip
                    label={selectedInvoice.status_invoice}
                    sx={{
                      bgcolor:
                        selectedInvoice.status_invoice === 'PAID'
                          ? '#05CD99'
                          : '#EE5D50',
                      color: '#fff',
                      fontWeight: 900,
                      px: 1,
                    }}
                  />
                </Stack>
              </Paper>

              {/* CLIENT & INFO GRID */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      color: '#A3AED0',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      mb: 1.5,
                    }}
                  >
                    Facturé à :
                  </Typography>

                  <Stack spacing={0.5}>
                    {/* NOM CLIENT */}
                    <Typography
                      sx={{
                        color: '#2B3674',
                        fontWeight: 800,
                        fontSize: '15px',
                      }}
                    >
                      {isIntervenant
                        ? selectedInvoice.client?.nom_client
                        : `${selectedInvoice.client?.nom_client} ${selectedInvoice.client?.prenom_client}`}
                    </Typography>

                    {/* EMAIL (caché intervenant) */}
                    {!isIntervenant && (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ color: '#707EAE' }}
                      >
                        <EmailIcon sx={{ fontSize: '14px' }} />
                        <Typography sx={{ fontSize: '13px' }}>
                          {selectedInvoice.client?.email_client}
                        </Typography>
                      </Stack>
                    )}

                    {/* PHONE (caché intervenant) */}
                    {!isIntervenant && (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ color: '#707EAE' }}
                      >
                        <LocalPhoneIcon sx={{ fontSize: '14px' }} />
                        <Typography sx={{ fontSize: '13px' }}>
                          {selectedInvoice.client?.phone_client}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={6}>
                  <Typography
                    sx={{
                      color: '#A3AED0',
                      fontSize: '12px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      mb: 1.5,
                    }}
                  >
                    Détails :
                  </Typography>
                  <Stack spacing={1}>
                    <DetailRow
                      label="Date"
                      value={new Date(
                        selectedInvoice.created_at
                      ).toLocaleDateString('fr-FR')}
                      icon={<CalendarMonthIcon sx={{ fontSize: 16 }} />}
                    />
                    <DetailRow
  label="Type"
  value={getTypeCmdLabel(selectedInvoice.commande?.type_cmd)}
  icon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
/>
                  </Stack>
                </Grid>
              </Grid>

              {/* FINANCIAL SUMMARY TABLE */}
              <Typography sx={{ color: '#2B3674', fontWeight: 800, mb: 2 }}>
                Récapitulatif financier
              </Typography>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  border: '1px solid #E9EDF7',
                  mb: 3,
                }}
              >
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ color: '#A3AED0', py: 2 }}>
                        Montant Hors Taxe (HT)
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: '#2B3674' }}
                      >
                        {Number(
                          selectedInvoice.commande?.total_htc_cmd
                        ).toFixed(3)}
                        €
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ color: '#A3AED0', py: 2 }}>
                        TVA (20%)
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 600, color: '#2B3674' }}
                      >
                        {Number(
                          selectedInvoice.commande?.total_tva_cmd
                        ).toFixed(3)}
                        €
                      </TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: '#F4F7FE' }}>
                      <TableCell
                        sx={{ color: '#2B3674', fontWeight: 800, py: 2 }}
                      >
                        Total TTC
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 900,
                          color: '#4318FF',
                          fontSize: '18px',
                        }}
                      >
                        {selectedInvoice.total_ca_invoice}€
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* FOOTER NOTE */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: '#FFFBF2',
                  border: '1px solid #FFE5B2',
                }}
              >
                <Typography
                  sx={{ color: '#7B5C00', fontSize: '12px', lineHeight: 1.5 }}
                >
                  * Cette facture est générée automatiquement. Pour toute
                  question relative au remboursement, veuillez contacter le
                  support avec la référence{' '}
                  <b>{selectedInvoice.ref_cmd_invoice}</b>.
                </Typography>
              </Box>
            </Box>

            {/* ACTIONS BARS */}
            <Box sx={{ p: 3, bgcolor: '#fff', borderTop: '1px solid #E9EDF7' }}>
              <Stack direction="row" spacing={2}>
                <button
                  onClick={() => handleDownloadPDF(selectedInvoice)} // Ajoutez ceci
                  style={{
                    flexGrow: 1,
                    padding: '12px',
                    borderRadius: '12px',
                    border: 'none',
                    backgroundColor: '#4318FF',
                    color: 'white',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Télécharger PDF
                </button>
              </Stack>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default Factures;
